import firebase from "firebase/compat/app";
export interface UpdatableAppUserJSON {
  name: string;
  country_id: string;
  state_id: string;
  city_id: string;
  neighbourhood_id: string | undefined;
  most_granular_geo_area_id: string;
}

export interface AppUserJSON extends UpdatableAppUserJSON {
  id: string;
  phone_number: string;
}

export interface UpdatableAppUserData {
  name: string | null;
  countryId: string | null;
  cityId: string | null;
  stateId: string | null;
  neighbourhoodId: string | null | undefined;
  mostGranularGeoAreaId: string | null;
}

export interface AppUserData extends UpdatableAppUserData {
  id: string;
  phoneNumber: string;
}

export class AppUser {
  private _id: string;
  private _phoneNumber: string;
  private _name: string | null;
  private _countryId: string | null;
  private _stateId: string | null;
  private _cityId: string | null;
  private _neighbourhoodId: string | null | undefined;
  private _mostGranularGeoAreaId: string | null;
  private _isComplete: boolean = false;

  constructor({
    id,
    name,
    phoneNumber,
    cityId,
    stateId,
    countryId,
    neighbourhoodId,
    mostGranularGeoAreaId,
  }: AppUserData) {
    this._id = id;
    this._name = name;
    this._phoneNumber = phoneNumber;
    this._countryId = countryId;
    this._stateId = stateId;
    this._cityId = cityId;
    this._neighbourhoodId = neighbourhoodId;
    this._mostGranularGeoAreaId = mostGranularGeoAreaId;
  }

  get id(): string {
    return this._id;
  }

  get phoneNumber(): string {
    return this._phoneNumber;
  }

  get name(): string | null {
    return this._name;
  }

  get countryId(): string | null {
    return this._countryId;
  }

  get stateId(): string | null {
    return this._stateId;
  }

  get cityId(): string | null {
    return this._cityId;
  }

  get neighbourhoodId(): string | null | undefined {
    return this._neighbourhoodId;
  }

  get mostGranularGeoAreaId(): string | null {
    return this._mostGranularGeoAreaId;
  }

  get isComplete(): boolean {
    return this._isComplete;
  }

  addUpdatableAppUserJSON(json: UpdatableAppUserJSON): void {
    this._name = json.name;
    this._countryId = json.country_id;
    this._stateId = json.state_id;
    this._cityId = json.city_id;
    this._neighbourhoodId = json.neighbourhood_id;
    this._mostGranularGeoAreaId = json.most_granular_geo_area_id;
  }

  updateIsComplete({
    name,
    countryId,
    stateId,
    cityId,
    cityHasNeighbourhoods,
    neighbourhoodId,
  }: {
    name: string | null;
    countryId: string | null;
    stateId: string | null;
    cityId: string | null;
    cityHasNeighbourhoods: boolean;
    neighbourhoodId: string | null | undefined;
  }): void {
    const isComplete =
      !!name &&
      !!countryId &&
      !!stateId &&
      !!cityId &&
      (cityHasNeighbourhoods ? !!neighbourhoodId : true);

    this._isComplete = isComplete;
  }

  copy(): AppUser | null {
    return new AppUser(this);
  }

  toUpdatableAppUserJSON(): UpdatableAppUserJSON {
    if (
      !this.name ||
      !this.countryId ||
      !this.stateId ||
      !this.cityId ||
      !this.mostGranularGeoAreaId
    ) {
      throw new Error(`Cannot update incomplete user: ${this}`);
    }

    return {
      name: `${this._name}`,
      country_id: `${this._countryId}`,
      state_id: `${this.stateId}`,
      city_id: `${this.cityId}`,
      neighbourhood_id: this._neighbourhoodId ? `${this._neighbourhoodId}` : "",
      most_granular_geo_area_id: `${this._mostGranularGeoAreaId}`,
    };
  }

  static fromFirebaseUser(firebaseUser: firebase.User): AppUser {
    if (!firebaseUser.phoneNumber) {
      throw new Error(
        `Cannot create user without a phone number: ${JSON.stringify(
          firebaseUser,
        )}`,
      );
    }

    return new AppUser({
      id: firebaseUser.uid,
      name: null,
      phoneNumber: firebaseUser.phoneNumber,
      countryId: null,
      stateId: null,
      cityId: null,
      neighbourhoodId: null,
      mostGranularGeoAreaId: null,
    });
  }
}
